/**
 * The sign-in door, in the place a signed-out customer used to be refused.
 *
 * ── WHAT THIS REPLACES ──────────────────────────────────────────────────────────
 * A customer who was not signed in — or whose session had quietly ended — opened
 * অ্যাকাউন্ট and was shown অনুমতি নেই: the one state drawn on founder mode's dark ground,
 * headed "ফাউন্ডার মোড", telling them their role was the finance department and that the
 * cap table was for founders. Nothing about that frame was true of them. They were not
 * refused anything; there was simply nobody signed in yet to show the page to.
 *
 * So "nobody is signed in" is not one of the eleven states, and this is not a state
 * frame. It is the door: say what is needed, and open it. `states/boundary.tsx` renders it
 * when the server refused for want of an identity AND this device holds no session
 * (`api/client.ts § refusalMeansSignedOut`) — never for a signed-in person whose role does
 * not reach something, which is অনুমতি নেই and exists only on internal surfaces.
 *
 * ── WHERE THE DOOR GOES ─────────────────────────────────────────────────────────
 * To A7 · লগইন ও পুনরুদ্ধার with `?next=` set to where the person was, so signing in
 * brings them back to the page that asked. On a FIRST RUN — nobody has ever signed in on
 * this device — to A1 · ভাষা নির্বাচন instead, which is where the product begins
 * (`auth/session.ts § WHERE A FIRST VISIT BEGINS`). A2 carries its own "আমার অ্যাকাউন্ট
 * আছে" for a person who does have an account from another phone.
 *
 * `next` is only ever a path this app produced from its own location — it starts with a
 * single `/` — and it is the login flow's job to refuse anything else when it reads it
 * back. An open redirect is a phishing link with our name on it.
 *
 * ── THE COPY IS AUTHORED, AND SAYS SO ────────────────────────────────────────────
 * No frame draws this card. The two headings and two bodies below were written for it and
 * no native speaker has read them: they carry `data-copy-status="authored"` into the DOM
 * and are listed for review in `docs/copy-review/W1-04.md`. The one exception is
 * `শুরু করুন`, A1's own button, reused so the first-run door names the step it opens.
 * They follow `P0 States § চারটি নিয়ম`: say what is needed, not a code; say the next
 * step; offer a way out — here, two (the door, and home) plus the tab bar.
 *
 * ── GEOMETRY ─────────────────────────────────────────────────────────────────────
 * The state frames' own vocabulary, borrowed rather than invented: the shared frame box,
 * the white header with the 20px back chevron (§ ৫, § ৯), the 56px bordered glyph square
 * and `600 22px/1.25` title of § ১০ — on sand, not on § ১০'s dark ground, because this
 * is a customer's screen — and § ৬'s full-width 48px buttons.
 */
import type { MouseEvent } from 'react';
import { Icon, TabBar, type TabBarKey } from '@oprappo/ui';
import type { CopyStatus } from '@oprappo/contracts';
import { StateFrame, BackChevron } from '../states/chrome.js';

/** Where the door opens for a person who has signed in on this device before. */
export const SIGN_IN_PATH = '/auth/login';
/** Where it opens for a device nobody has signed in on. `auth/session.ts § FIRST_RUN_PATH`. */
export const FIRST_RUN_SIGN_IN_PATH = '/welcome/language';

/**
 * The card's Bangla. `copy_status: 'authored'` — written for this card, unreviewed.
 * `start_bn` is the exception and is marked as such: A1's drawn button, transcribed.
 */
export const SIGN_IN_PROMPT_COPY = {
  copy_status: 'authored' satisfies CopyStatus,
  /** A person who has an account on this phone, or had one. */
  returning: {
    title_bn: 'এই পাতা দেখতে লগইন করুন',
    body_bn: 'আপনার অর্ডার, ঠিকানা আর অ্যাকাউন্টের তথ্য শুধু আপনিই দেখতে পারেন। মোবাইল নম্বর দিয়ে লগইন করুন।',
    action_bn: 'লগইন করুন',
  },
  /** Nobody has ever signed in on this phone. */
  first_run: {
    title_bn: 'এই পাতা দেখতে অ্যাকাউন্ট লাগবে',
    body_bn: 'এই ফোনে আগে কখনও লগইন করা হয়নি। শুরু করতে ভাষা বেছে নিন, তারপর মোবাইল নম্বর দিন।',
    /** `Oprappo P0 Screens.dc.html` A1's own button. Sourced, not authored. */
    action_bn: 'শুরু করুন',
  },
  home_bn: 'হোমে যান',
} as const;

/**
 * The address the door opens, for a person who was at `returnTo`.
 *
 * `returnTo` that is not an app path — empty, absolute, protocol-relative — is dropped
 * rather than passed on: the login flow should never be handed somewhere else to send
 * a person after they have just proved who they are.
 */
export function signInHref(returnTo: string, firstRun: boolean): string {
  if (firstRun) return FIRST_RUN_SIGN_IN_PATH;
  const safe = returnTo.startsWith('/') && !returnTo.startsWith('//');
  return safe ? `${SIGN_IN_PATH}?next=${encodeURIComponent(returnTo)}` : SIGN_IN_PATH;
}

export interface SignInPromptProps {
  /** The path and query the person was on — where signing in should bring them back. */
  returnTo: string;
  /** Nobody has signed in on this device: open A1 rather than A7. */
  firstRun: boolean;
  /** In-app navigation. The door is also a real link, so it works before this runs. */
  onNavigate: (to: string) => void;
  onBack: () => void;
  /** The customer tab this screen sits under, when it sits under one. */
  tab?: TabBarKey | undefined;
  onTab?: ((key: TabBarKey) => void) | undefined;
}

export function SignInPrompt({
  returnTo,
  firstRun,
  onNavigate,
  onBack,
  tab,
  onTab,
}: SignInPromptProps): JSX.Element {
  const copy = firstRun ? SIGN_IN_PROMPT_COPY.first_run : SIGN_IN_PROMPT_COPY.returning;
  const href = signInHref(returnTo, firstRun);
  const open = (event: MouseEvent<HTMLAnchorElement>): void => {
    event.preventDefault();
    onNavigate(href);
  };

  return (
    <StateFrame stateKey="sign_in">
      <div className="bg-white border-b border-border px-[16px] py-[14px] flex gap-[12px] items-center">
        <button
          type="button"
          aria-label="পিছনে যান"
          onClick={onBack}
          className="bg-transparent border-0 p-0 rounded-none flex-none"
        >
          <BackChevron />
        </button>
      </div>

      <div
        data-sign-in-prompt={firstRun ? 'first_run' : 'returning'}
        data-copy-status={SIGN_IN_PROMPT_COPY.copy_status}
        className="flex-1 px-[16px] py-[20px] flex flex-col"
      >
        <div className="box-content w-[56px] h-[56px] border-2 border-teal flex items-center justify-center mb-[22px]">
          <Icon name="account" size={26} />
        </div>
        {/* 22px Bangla sits in the display band: leading 1.25, not 1.75 (docs/02). */}
        <h1 className="m-0 font-anek font-semibold text-[22px] leading-[1.25] text-ink">{copy.title_bn}</h1>
        <div className="font-anek font-normal text-[15px] leading-[1.75] text-body mt-[10px]">{copy.body_bn}</div>

        <div className="flex-1" />

        <a
          href={href}
          onClick={open}
          className="w-full bg-teal h-[50px] flex items-center justify-center font-anek font-semibold text-[15px] leading-[1.75] text-white no-underline"
        >
          {copy.action_bn}
        </a>
        <button
          type="button"
          onClick={() => onNavigate('/')}
          className="w-full h-[48px] p-0 mt-[8px] flex items-center justify-center font-anek font-semibold text-[15px] leading-[1.75] text-teal bg-transparent border-0 rounded-none"
        >
          {SIGN_IN_PROMPT_COPY.home_bn}
        </button>
      </div>

      {tab === undefined ? null : <TabBar activeKey={tab} onChange={onTab} />}
    </StateFrame>
  );
}
